import React from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { TouchBackend } from 'react-dnd-touch-backend'
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form'
import { FormItemProps, SelectProps } from 'antd'
import { DefaultOptionType, SelectValue } from 'antd/lib/select'
import cl from 'classnames'

import { MultiselectWithDraggableItems } from 'Shared/atoms'
import useSmallScreen from 'Shared/hooks/useSmallScreen'
import { hasError } from 'Shared/utils/validation'

import { FieldNameRegexType } from 'Core/interfaces'

import FormItem from './FormItem'

import styles from './input.module.scss'

export type FormSelectProps<FieldsType extends FieldValues> = SelectProps<SelectValue> &
	FormItemProps & {
		label?: string
		wrapperClassName?: string
		multiple?: boolean
		control: Control<FieldsType>
		fieldName: keyof FieldsType | FieldNameRegexType
		onChange?: (value: SelectValue, option: DefaultOptionType[] | DefaultOptionType) => void
		allowSelectTyping?: boolean
		useFullOption?: boolean
		inputClassName?: string
		showTooltip?: boolean
		onBlurSelect?: () => void
		showPlaceholderWithEmptyValue?: boolean
		onUpdateValueArray: (value: LabelValue<number>[]) => void
	}

function FormSelect<FieldsType extends FieldValues>({
	extra,
	label,
	multiple,
	wrapperClassName,
	control,
	fieldName,
	onChange,
	inputClassName,
	useFullOption = false,
	showTooltip = false,
	onBlurSelect,
	showPlaceholderWithEmptyValue,
	onSelect,
	onUpdateValueArray,
	...props
}: FormSelectProps<FieldsType>) {
	const isSmallScreen = useSmallScreen()

	return (
		<DndProvider backend={isSmallScreen ? TouchBackend : HTML5Backend}>
			<Controller
				name={fieldName as FieldPath<FieldsType>}
				control={control}
				render={({ field: { onChange, name, ...field }, fieldState: { error } }) => (
					<FormItem
						className={cl(styles.inputWrapper, styles.select, wrapperClassName)}
						label={label}
						error={hasError(error)}
						colon={false}
						name={name}
						extra={extra}
					>
						<MultiselectWithDraggableItems
							{...props}
							{...field}
							onUpdateValueArray={onUpdateValueArray}
							onChange={onChange}
							dataTestId={`qaa-${name}`}
						/>
					</FormItem>
				)}
			/>
		</DndProvider>
	)
}

export default FormSelect
